import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createTheme,
  ThemeProvider,
  Box,
  Toolbar,
  Container,
  Grid,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { getClientById, updateClient } from "../../../api/clientApi";
import FormTextFieldClient from "./formTextFieldClient";
import { EditClienteData } from "../../../types/clientData";
import { formatDocument, formatPhone } from "../../../services/formatDocument";
import { LoadingButton } from "@mui/lab";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

const styles = {
  formControl: {
    fullWidth: true,
    marginTop: -2,
    "& .MuiSelect-root": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
      "&:focus": {
        backgroundColor: "#ffddec",
        borderColor: "brown",
      },
      "&:before": {
        borderColor: "orange",
      },
      "&:after": {
        borderColor: "green",
      },
    },
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      color: "#303030",
      borderWidth: "2px",
    },
  },
  button: {
    height: 50,
    marginTop: 2,
    backgroundColor: "#303030",
    borderRadius: 2,
    alignItems: "center",
    ":hover": {
      bgcolor: "#5c5c5c",
    },
  },
};

enum FormField {
  Nome = "Nome",
  DocumentoIdentificador = "Documento Identificador",
  TelefonedeContato = "Telefone de Contato",
}

interface FormValues {
  [FormField.Nome]: string;
  [FormField.DocumentoIdentificador]: string | number;
  [FormField.TelefonedeContato]: string | number;
}

const EditClient: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
  const [formValues, setFormValues] = useState<FormValues>({
    [FormField.Nome]: "",
    [FormField.DocumentoIdentificador]: "",
    [FormField.TelefonedeContato]: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (token && clientId) {
          const clientData = await getClientById(token, clientId);
          setFormValues({
            [FormField.Nome]: clientData.nome_cliente,
            [FormField.DocumentoIdentificador]:
              clientData.documento_identificador_cliente.toString(),
            [FormField.TelefonedeContato]:
              clientData.telefone_contato_cliente.toString(),
          });
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes do client:", error);
      }
    };

    fetchData();
  }, [token, clientId]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string
  ) => {
    const { name, value } = event.target;

    let formattedValue =
      name === FormField.DocumentoIdentificador
        ? formatDocument(value)
        : name === FormField.TelefonedeContato
        ? formatPhone(value)
        : value;

    if (name === FormField.TelefonedeContato) {
      formattedValue = formattedValue.slice(0, 14);
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldName]: formattedValue,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (token && clientId) {
        const clientData: EditClienteData = {
          nome_cliente: formValues[FormField.Nome],
          documento_identificador_cliente: String(
            formValues[FormField.DocumentoIdentificador]
          ),
          telefone_contato_cliente: String(
            formValues[FormField.TelefonedeContato]
          ),
        };

        await updateClient(token, clientId, clientData);
        toast.success("Cliente atualizado com sucesso!");
        navigate("/cliente");
      } else {
        toast.error("ID do cliente não encontrado!");
      }
    } catch (error) {
      toast.error("Não foi possível atualizar os detalhes do cliente!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ mb: 2, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/cliente")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>

          <Container sx={{ mb: 4 }} maxWidth={"lg"}>
            <Box component="form" noValidate>
              <Typography
                variant="h5"
                fontStyle={"inherit"}
                sx={{ fontWeight: 400 }}
              >
                Editar Cliente
                <Divider
                  variant="middle"
                  sx={{
                    width: 120,
                    backgroundColor: "red",
                    height: 2,
                    marginLeft: 0,
                  }}
                />
              </Typography>
              <Grid container spacing={3} mt={-2} p={2}>
                {Object.entries(formValues).map(([fieldName, fieldValue]) => (
                  <Grid item xs={12} sm={6} key={fieldName}>
                    <FormTextFieldClient
                      label={fieldName}
                      name={fieldName}
                      value={fieldValue}
                      disabled={fieldName === FormField.DocumentoIdentificador}
                      onChange={(e) => handleChange(e, fieldName)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <LoadingButton
              variant="contained"
              fullWidth
              loading={loading}
              sx={styles.button}
              onClick={handleSubmit}
            >
              <Typography>Atualizar</Typography>
            </LoadingButton>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default EditClient;
